import { Component, Directive, Input, OnInit } from '@angular/core'
import { select, Store } from '@ngrx/store'
import * as lodash from 'lodash'
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs'
import { debounceTime, map, switchMap } from 'rxjs/operators'
import { Destroyable, selectAllUsers, untilDestroy, User, userFullname } from '@tokeet-frontend/tv3-platform'
import { ContractSignerType, Signer } from '@tokeet-frontend/signature'
import { Guest } from '@tv3/store/guest/guest.model'
import { GuestService } from '@tv3/store/guest/guest.service'

export interface SignerOptionItem {
  type: ContractSignerType
  name: string
  email: string
  id: string
  role: string
  order: number
  data: any
  id_photo_required: boolean
  selfie_photo_required: boolean
}

export function getContractSigners(signers: SignerOptionItem[], isRoleCustomizable = false): Partial<Signer>[] {
  return lodash.map(signers, (signer: SignerOptionItem, index: number) => {
    const data = signer.data
    const isSelectedSigner = signer.email === data?.primaryEmail
    return {
      email: signer.email,
      name: signer.name,
      id: isSelectedSigner ? signer.id : undefined,
      type: isSelectedSigner ? signer.type : ContractSignerType.Custom,
      role: isRoleCustomizable ? signer.name : signer.role,
      order: signer.order,
      id_photo_required: signer.id_photo_required,
      selfie_photo_required: signer.selfie_photo_required,
    }
  })
}

export function userToSignerFormData(item: User, role?: string) {
  return {
    type: ContractSignerType.Staff,
    name: userFullname(item),
    email: item.primaryEmail,
    id: item.id,
    role: role || userFullname(item),
  }
}

export function guestToSignerFormData(item: Guest, role?: string) {
  return {
    type: ContractSignerType.Guest,
    name: lodash.toString(item.name),
    email: item.primaryEmail,
    id: item.id,
    role: role || item.name,
  }
}

@Directive()
export class SignerInputBaseComponent extends Destroyable {
  @Input() isGuestEnabled = true
  @Input() isUserEnabled = true
  @Input() role: string

  users$ = this.store.pipe(select(selectAllUsers))
  search$ = new Subject<string>()

  options$ = new BehaviorSubject<SignerOptionItem[]>([])

  constructor(protected store: Store<any>, protected guestService: GuestService) {
    super()
  }

  init(): void {
    this.search$
      .pipe(
        debounceTime(300),
        map((term: string) => lodash.toLower(term || '').trim()),
        switchMap((term: string) =>
          combineLatest([
            !this.isGuestEnabled
              ? of([])
              : this.searchGuests(term).pipe(
                  map((guests) =>
                    lodash.map(guests, (item) => ({
                      ...guestToSignerFormData(item, this.role),
                      data: item,
                    }))
                  )
                ),
            !this.isUserEnabled
              ? of([])
              : this.searchUsers(term).pipe(
                  map((users) =>
                    lodash.map(users, (item) => ({
                      ...userToSignerFormData(item, this.role),
                      data: item,
                    }))
                  )
                ),
          ]).pipe(
            map(([options1, options2]) =>
              lodash.orderBy([...options1, ...options2], ['type', (t) => lodash.toLower(t.name)], ['desc', 'asc'])
            )
          )
        ),
        untilDestroy(this)
      )
      .subscribe((items) => {
        this.options$.next(items)
      })
  }

  searchGuests(term: string): Observable<Guest[]> {
    return this.guestService.search(term, 15, 0)
  }

  searchUsers(term: string): Observable<User[]> {
    return this.users$.pipe(
      map((users: User[]) =>
        lodash.filter(users, (user) => {
          const str = lodash.toLower(userFullname(user))
          return str.search(term) > -1
        })
      )
    )
  }
}
